import {
  atom,
  batch,
  reatomAsync,
  withDataAtom,
  withStatusesAtom,
} from "@reatom/framework";
import {
  ArbitrageInfo,
  CreateOrderInterface,
  OrderDocuments,
  OrderHistory,
  OrdersInterface,
} from "./types";
import {
  arbitrageOrder,
  cancelOrder,
  createOrder,
  doneOrder,
  fixOrder,
  getArbitrageInfo,
  getCurrentOrder,
  getDocuments,
  getHistory,
  getOrders,
  paymentOrder,
} from "@/core/api/requests/orders";

export const currentOrderAtom = atom<CreateOrderInterface | null>(
  null,
  "currentOrderAtom"
);

export const isLoadingCurrentOrderAtom = atom<boolean>(
  false,
  "isLoadingCurrentOrderAtom"
);

export const currentOrderArbitrageAtom = atom<ArbitrageInfo | null>(
  null,
  "currentOrderArbitrageAtom"
);

export const isLoadingArbitrageAtom = atom<boolean>(
  false,
  "isLoadingArbitrageAtom"
);
export const orderDocumentsAtom = atom<OrderDocuments | null>(
  null,
  "orderDocumentsAtom"
);
export const ordersAtom = atom<OrdersInterface | null>(null, "ordersAtom");

export const orderHistory = atom<OrderHistory | null>(null, "orderHistory");
export const isLoadingOrderHistory = atom<boolean>(
  false,
  "isLoadingOrderHistory"
);

export const orderDocumentsAction = reatomAsync(
  async (ctx, orderId: number) => {
    return ctx.schedule(() => getDocuments(orderId));
  },
  {
    name: "orderDocumentsAction",
    onFulfill: (ctx, response) => {
      orderDocumentsAtom(ctx, response);
    },
  }
);
export const getOrderHistoryAction = reatomAsync(
  async (ctx, oid: number, limit?: number) => {
    isLoadingOrderHistory(ctx, true);
    const response = await getHistory(oid, limit);
    return response;
  },
  {
    name: "createOrderAction",
    onFulfill: (ctx, response) => {
      orderHistory(ctx, response);
      isLoadingOrderHistory(ctx, false);
    },
    onReject: (ctx) => {
      isLoadingOrderHistory(ctx, false);
    },
  }
);

export const createOrderAction = reatomAsync(
  async (_ctx, { cardId, extra }: { cardId: number; extra: number[] }) => {
    const response = await createOrder(cardId, extra);
    return response;
  },
  {
    name: "createOrderAction",
    onFulfill: (ctx, response) => {
      currentOrderAtom(ctx, response);
      getOrderHistoryAction(ctx, response.id);
      orderDocumentsAction(ctx, response.id);
    },
  }
);

export const getOrdersAction = reatomAsync(
  async () => {
    const response = await getOrders({});
    return response;
  },
  {
    name: "createOrderAction",
    onFulfill: (ctx, response) => {
      ordersAtom(ctx, response);
    },
  }
);

export const checkArbitrageAction = reatomAsync(
  async (ctx, { orderId }: { orderId: number }) => {
    isLoadingArbitrageAtom(ctx, true);
    const response = await getArbitrageInfo(orderId);
    return response;
  },
  {
    name: "checkArbitrageAction",
    onFulfill: (ctx, response) => {
      currentOrderArbitrageAtom(ctx, response);
      isLoadingArbitrageAtom(ctx, false);
    },
    onReject: (ctx) => {
      isLoadingArbitrageAtom(ctx, false);
      currentOrderArbitrageAtom(ctx, null);
    },
  }
);

export const getCurrentOrderAction = reatomAsync(
  async (ctx, { orderId }: { orderId: number }) => {
    batch(ctx, () => {
      paymentLinkAsync.dataAtom(ctx, "");
      currentOrderAtom(ctx, null);
      isLoadingCurrentOrderAtom(ctx, true);
      getOrderHistoryAction(ctx, orderId);
    });
    return ctx.schedule(() => getCurrentOrder(orderId));
  },
  {
    name: "getCurrentOrderAction",
    onFulfill: (ctx, response) => {
      checkArbitrageAction(ctx, { orderId: response.id });
      currentOrderAtom(ctx, response);
      orderDocumentsAction(ctx, response.id);
      isLoadingCurrentOrderAtom(ctx, false);
    },
    onReject: (ctx) => {
      isLoadingCurrentOrderAtom(ctx, false);
    },
  }
);

export const paymentLinkAsync = reatomAsync(async (ctx, orderId: number) => {
  return ctx.schedule(() => paymentOrder(orderId));
}, "paymentLinkAsync").pipe(withDataAtom(""), withStatusesAtom());

export const cancelOrderAction = reatomAsync(
  async (ctx, orderId: number) => {
    batch(ctx, () => {
      paymentLinkAsync.dataAtom(ctx, "");
      currentOrderAtom(ctx, null);
      isLoadingCurrentOrderAtom(ctx, true);
    });
    return ctx.schedule(() => cancelOrder(orderId));
  },
  {
    name: "cancelOrderAction",
    onFulfill: (ctx, response) => {
      currentOrderAtom(ctx, response);
      isLoadingCurrentOrderAtom(ctx, false);
    },
    onReject: (ctx) => {
      isLoadingCurrentOrderAtom(ctx, false);
    },
  }
);
export const doneOrderAction = reatomAsync(
  async (ctx, orderId: number) => {
    batch(ctx, () => {
      paymentLinkAsync.dataAtom(ctx, "");
      currentOrderAtom(ctx, null);
      isLoadingCurrentOrderAtom(ctx, true);
    });
    return ctx.schedule(() => doneOrder(orderId));
  },
  {
    name: "doneOrderAction",
    onFulfill: (ctx, response) => {
      currentOrderAtom(ctx, response);
      isLoadingCurrentOrderAtom(ctx, false);
    },
    onReject: (ctx) => {
      isLoadingCurrentOrderAtom(ctx, false);
    },
  }
);

export const fixOrderAction = reatomAsync(
  async (ctx, orderId: number) => {
    batch(ctx, () => {
      paymentLinkAsync.dataAtom(ctx, "");
      currentOrderAtom(ctx, null);
      isLoadingCurrentOrderAtom(ctx, true);
    });
    return ctx.schedule(() => fixOrder(orderId));
  },
  {
    name: "fixOrderAction",
    onFulfill: (ctx, response) => {
      currentOrderAtom(ctx, response);
      isLoadingCurrentOrderAtom(ctx, false);
    },
    onReject: (ctx) => {
      isLoadingCurrentOrderAtom(ctx, false);
    },
  }
);

export const sendToArbitrage = reatomAsync(
  async (ctx, orderId: number, message: string) => {
    batch(ctx, () => {
      currentOrderAtom(ctx, null);
      isLoadingCurrentOrderAtom(ctx, true);
      isLoadingArbitrageAtom(ctx, true);
    });
    return ctx.schedule(() => arbitrageOrder(orderId, message));
  },
  {
    name: "sendToArbitrage",
    onFulfill: (ctx, response) => {
      checkArbitrageAction(ctx, { orderId: response.id });
      isLoadingCurrentOrderAtom(ctx, false);
    },
    onReject: (ctx) => {
      isLoadingCurrentOrderAtom(ctx, false);
    },
  }
);

// getHistory
